import React, { FC, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ISinglePro } from './state';
import { cldUrl } from '../../utils/cloudinary';
import { IconAgency, IconPerson, IconPlayBox } from '../../utils/icons';
import { formatProfessionWithExperience } from '../../utils/professionExperience';
import { isAM, isProTeam, isInternal } from '../../utils';
import { useUser } from '../../hooks/useUser';
import InternalPanel from './InternalPanel';
import VideoIntroModal from './VideoIntroModal';
import { usePortalPath } from '../../hooks/usePortalPath'

const BLACK_FAMILY = 'CircularStdBlack, Helvetica, Arial, sans-serif';
const railBtn =
  'w-full flex items-center justify-center gap-2 rounded-xl px-3.5 py-3 text-[13px] font-bold transition-colors';

interface Props {
  expert?: ISinglePro;
  isSharePage?: boolean;
}

/** Sticky left rail: portrait, identity, actions, internal-only panel. */
const IdentityRail: FC<Props> = ({ expert, isSharePage }) => {
  const { role } = useUser();
  const navigate = useNavigate();
  const { p } = usePortalPath();
  const [showVideo, setShowVideo] = useState(false);

  const professionLines = useMemo(
    () =>
      (expert?.professions || [])
        .map((p) => formatProfessionWithExperience(p))
        .filter(Boolean) as string[],
    [expert]
  );

  const location = [expert?.city, expert?.state].filter(Boolean).join(', ');
  const canEdit = !isSharePage && (isAM(role) || isProTeam(role));

  return (
    <Box className="flex flex-col gap-5 md:sticky md:top-6">
      <Box
        className="bg-surface border border-border-token rounded-xl overflow-hidden"
        style={{ boxShadow: 'var(--sh-2)' }}
      >
        {expert?.avatar ? (
          <img
            alt={expert?.displayName || 'Expert portrait'}
            src={cldUrl(expert.avatar, { w: 616, h: 600, crop: 'fill', gravity: 'face' })}
            className="w-full h-[300px] object-cover"
          />
        ) : (
          <Box className="h-[300px] bg-tint flex items-center justify-center">
            <IconPerson width={64} height={64} />
          </Box>
        )}
        <Box className="p-[18px] flex flex-col gap-3.5">
          <Box className="flex flex-col gap-1.5">
            <Box className="flex items-start gap-2">
              <Typography
                sx={{
                  fontFamily: BLACK_FAMILY,
                  fontSize: 24,
                  lineHeight: 1.1,
                  letterSpacing: '-.02em',
                  color: 'var(--text)',
                  overflowWrap: 'anywhere',
                }}
              >
                {expert?.displayName}
              </Typography>
              {!isSharePage && !!expert?.agencyType && (
                <span title="Agency" className="mt-1 shrink-0">
                  <IconAgency size={18} fill="var(--text-2)" />
                </span>
              )}
            </Box>
            {professionLines.length > 0 && (
              <Box className="flex flex-col gap-0.5">
                {professionLines.map((line) => (
                  <Typography
                    key={line}
                    sx={{ fontSize: 13, lineHeight: 1.5, color: 'var(--text-2)' }}
                  >
                    {line}
                  </Typography>
                ))}
              </Box>
            )}
            {!isSharePage && !!location && (
              <Typography sx={{ fontSize: 12.5, lineHeight: 1.4, color: 'var(--muted)' }}>
                {location}
              </Typography>
            )}
          </Box>

          {(!!expert?.video1 || canEdit) && <div className="h-px bg-border-token" />}

          {(!!expert?.video1 || canEdit) && (
            <Box className="flex flex-col gap-2">
              {!!expert?.video1 && (
                <button
                  type="button"
                  className={`${railBtn} bg-accent text-rail hover:opacity-90`}
                  onClick={() => setShowVideo(true)}
                >
                  <IconPlayBox size={14} />
                  Video intro
                </button>
              )}
              {canEdit && (
                <button
                  type="button"
                  className={`${railBtn} bg-surface border border-border-token text-text-1 hover:bg-surface-2`}
                  onClick={() => navigate(p(`/experts/${expert?.userid}/${expert?.slug}/edit`))}
                >
                  Edit expert
                </button>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {!isSharePage && isInternal(role) && <InternalPanel expert={expert} />}

      <VideoIntroModal
        open={showVideo}
        onClose={() => setShowVideo(false)}
        name={expert?.displayName}
        src={expert?.video1}
      />
    </Box>
  );
};

export default IdentityRail;
